import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Form,
  InputGroup,
  Nav,
  Spinner,
  Tab,
  Table,
} from "react-bootstrap";
import {
  CheckCircle,
  AlertTriangle,
  AlertOctagon,
  AlertCircle,
  Download,
  Smartphone,
} from "react-feather";
import {
  IKindredDealSummary,
  IKindredOfferSummary,
  IKindredDomainGeoResponse,
  ICouponResultsResponse,
  ICouponResultWithMerchant,
  ISamsungCheckResponse,
} from "../../interfaces";
import { useGet } from "../../api";
import { format, subDays } from "date-fns";
import { Link } from "react-router";
import {
  TargetingPanel,
  CouponWithStatus,
  EntityStatus,
  computeStatus,
  entityStatusBadge,
  statusPriority,
  filterByStatus,
  STATUS_OPTIONS,
  downloadCouponsCsv,
  downloadDealsCsv,
  downloadOffersCsv,
  exportFilename,
  StatusFilter,
} from "../../components/workbench";

interface ReviewDataPanelProps {
  data: IKindredDomainGeoResponse | null;
  loading: boolean;
  selectedDomain: string | null;
  selectedCountryCode: string | null;
  selectedEntity: { type: "deal" | "coupon" | "offer"; id: string } | null;
  onSelectEntity: (type: "deal" | "coupon" | "offer", id: string) => void;
  onMarkTested: (comment: string) => Promise<void>;
  markingTested: boolean;
  lastTestedAt: string | null;
  testedByName: string | null;
}

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "—";
  try {
    return format(new Date(dateStr), "yyyy-MM-dd");
  } catch {
    return dateStr;
  }
};

export const ReviewDataPanel: React.FC<ReviewDataPanelProps> = ({
  data,
  loading,
  selectedDomain,
  selectedCountryCode,
  selectedEntity,
  onSelectEntity,
  onMarkTested,
  markingTested,
  lastTestedAt,
  testedByName,
}) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("LIVE");
  const [activeTab, setActiveTab] = useState<string>("deals");
  const [testComment, setTestComment] = useState("");

  // Samsung eligibility check
  const samsungUrl = useMemo(() => {
    if (!selectedDomain || !selectedCountryCode) return null;
    return `kindred/samsung-check?domain=${encodeURIComponent(selectedDomain)}&countryCode=${encodeURIComponent(selectedCountryCode)}`;
  }, [selectedDomain, selectedCountryCode]);

  const { data: samsungData, loading: samsungLoading } =
    useGet<ISamsungCheckResponse>(samsungUrl, !!samsungUrl);

  // Fetch recent coupon results (last 24h) for flagging live coupons
  const recentResultsUrl = useMemo(() => {
    if (!selectedDomain) return null;
    const params = new URLSearchParams();
    params.set("merchantUrl", selectedDomain);
    if (selectedCountryCode) params.set("countryCode", selectedCountryCode);
    params.set("startDate", subDays(new Date(), 1).toISOString());
    params.set("limit", "100");
    return `results/coupons?${params.toString()}`;
  }, [selectedDomain, selectedCountryCode]);

  const { data: recentResults } = useGet<ICouponResultsResponse>(
    recentResultsUrl,
    !!recentResultsUrl
  );

  // Build a set of coupon codes that had failures/errors in the last 24h
  const flaggedCouponCodes = useMemo(() => {
    const codes = new Set<string>();
    if (!recentResults?.results) return codes;
    for (const r of recentResults.results) {
      if (r.confirmedFailure || r.systemError) {
        codes.add(r.code.toUpperCase());
      }
    }
    return codes;
  }, [recentResults]);

  // Auto-switch tab when an entity is selected from a different tab
  useEffect(() => {
    if (selectedEntity) {
      const tabMap: Record<string, string> = {
        deal: "deals",
        coupon: "coupons",
        offer: "offers",
      };
      const targetTab = tabMap[selectedEntity.type];
      if (targetTab && targetTab !== activeTab) {
        setActiveTab(targetTab);
      }
    }
  }, [selectedEntity]);

  // Pre-compute statuses so badge counts match the filter
  const deals = useMemo(
    () =>
      (data?.deals || []).map((d) => ({
        ...d,
        _status: computeStatus(
          d.publishedStatus,
          d.startDate,
          d.endDate,
          d.suspended
        ),
      })),
    [data?.deals]
  );
  const coupons = useMemo(
    () =>
      (data?.coupons || []).map((c) => ({
        ...c,
        _status: computeStatus(c.publishedStatus, c.validFrom, c.validTo),
      })),
    [data?.coupons]
  );
  const offers = useMemo(
    () =>
      (data?.offers || []).map((o) => ({
        ...o,
        _status: computeStatus(o.publishedStatus, o.validFrom, o.validTo),
      })),
    [data?.offers]
  );

  const filteredDeals = useMemo(
    () => filterByStatus(deals, statusFilter),
    [deals, statusFilter]
  );
  const filteredCoupons = useMemo(
    () => filterByStatus(coupons, statusFilter),
    [coupons, statusFilter]
  );
  const filteredOffers = useMemo(
    () => filterByStatus(offers, statusFilter),
    [offers, statusFilter]
  );

  // Count entities per status scoped to the active tab
  const statusCounts = useMemo(() => {
    const items =
      activeTab === "coupons"
        ? coupons
        : activeTab === "offers"
          ? offers
          : deals;
    const counts: Record<string, number> = { ALL: items.length };
    for (const i of items) {
      counts[i._status] = (counts[i._status] || 0) + 1;
    }
    return counts;
  }, [deals, coupons, offers, activeTab]);

  if (!selectedDomain) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100 text-muted">
        Select a domain/country from the list
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading data...
      </div>
    );
  }

  if (!data) {
    return <div className="text-muted text-center py-4">No data available</div>;
  }

  return (
    <>
      <Tab.Container
        activeKey={activeTab}
        onSelect={(k) => k && setActiveTab(k)}
      >
        {/* Samsung indicator */}
        {samsungLoading ? (
          <div className="d-flex align-items-center px-2 py-1 border-bottom bg-light">
            <Spinner animation="border" size="sm" className="me-1" />
            <span className="small text-muted">Checking Samsung...</span>
          </div>
        ) : samsungData ? (
          <div
            className={`d-flex align-items-center px-2 py-1 border-bottom ${samsungData.showForSamsung ? "bg-success bg-opacity-10" : "bg-danger bg-opacity-10"}`}
          >
            <Smartphone size={14} className="me-1" />
            <span className="small fw-bold me-2">Samsung:</span>
            {samsungData.showForSamsung ? (
              <Badge bg="success">Eligible</Badge>
            ) : (
              <Badge bg="danger">Not Eligible</Badge>
            )}
            {samsungData.automaticFlow.eligible && (
              <Badge bg="info" className="ms-1">
                Auto ({samsungData.automaticFlow.sitewideCouponCount} sitewide,{" "}
                {samsungData.automaticFlow.templateCount} templates)
              </Badge>
            )}
            {samsungData.manualFlow.eligible && (
              <Badge bg="primary" className="ms-1">
                Manual (
                {samsungData.manualFlow.listType === "coupons_only"
                  ? `${samsungData.manualFlow.couponCount} coupons`
                  : samsungData.manualFlow.listType === "offers_only"
                    ? `${samsungData.manualFlow.offerCount} offers`
                    : `${samsungData.manualFlow.couponCount} coupons + ${samsungData.manualFlow.offerCount} offers`}
                )
              </Badge>
            )}
            {!samsungData.hasDeal && (
              <span className="small text-danger ms-2">No deal</span>
            )}
          </div>
        ) : null}

        <div className="d-flex align-items-center border-bottom px-2">
          <Nav variant="tabs" className="border-bottom-0 flex-grow-1">
            <Nav.Item>
              <Nav.Link eventKey="deals">
                Deals <Badge bg="primary">{filteredDeals.length}</Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="coupons">
                Coupons <Badge bg="info">{filteredCoupons.length}</Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="offers">
                Offers{" "}
                <Badge bg="warning" text="dark">
                  {filteredOffers.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="agentic">
                Agentic Testing{" "}
                {recentResults && recentResults.pagination.total > 0 && (
                  <Badge bg="danger">{recentResults.pagination.total}</Badge>
                )}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="targeting">Targeting</Nav.Link>
            </Nav.Item>
          </Nav>
          {activeTab !== "agentic" && activeTab !== "targeting" && (
            <Form.Select
              size="sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              style={{ width: 160 }}
              className="ms-2"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label} ({statusCounts[o.value] || 0})
                </option>
              ))}
            </Form.Select>
          )}
          {(activeTab === "deals" ||
            activeTab === "coupons" ||
            activeTab === "offers") && (
            <Button
              size="sm"
              variant="outline-secondary"
              className="ms-1"
              disabled={!statusCounts.ALL}
              onClick={() => {
                const name = exportFilename(
                  activeTab,
                  selectedDomain,
                  selectedCountryCode
                );
                if (activeTab === "coupons") downloadCouponsCsv(coupons, name);
                else if (activeTab === "deals") downloadDealsCsv(deals, name);
                else downloadOffersCsv(offers, name);
              }}
              title={`Download all ${statusCounts.ALL || 0} ${activeTab} on this domain as CSV, every status, with their current and published status`}
            >
              <Download size={13} />
            </Button>
          )}
        </div>

        <Tab.Content style={{ flex: 1, overflow: "auto" }}>
          <Tab.Pane eventKey="deals" className="p-2">
            <DealsTable
              deals={filteredDeals}
              onSelect={(id) => onSelectEntity("deal", id)}
              selectedId={
                selectedEntity?.type === "deal" ? selectedEntity.id : null
              }
            />
          </Tab.Pane>
          <Tab.Pane eventKey="coupons" className="p-2">
            <CouponsTable
              coupons={filteredCoupons}
              onSelect={(id) => onSelectEntity("coupon", id)}
              selectedId={
                selectedEntity?.type === "coupon" ? selectedEntity.id : null
              }
              flaggedCodes={flaggedCouponCodes}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="offers" className="p-2">
            <OffersTable
              offers={filteredOffers}
              onSelect={(id) => onSelectEntity("offer", id)}
              selectedId={
                selectedEntity?.type === "offer" ? selectedEntity.id : null
              }
            />
          </Tab.Pane>
          <Tab.Pane eventKey="agentic" className="p-2">
            <AgenticTestingPanel
              domain={selectedDomain}
              countryCode={selectedCountryCode}
              liveCoupons={coupons.filter((c) => c._status === "LIVE")}
              onSelectCoupon={(id) => onSelectEntity("coupon", id)}
              selectedCouponId={
                selectedEntity?.type === "coupon" ? selectedEntity.id : null
              }
            />
          </Tab.Pane>
          <Tab.Pane eventKey="targeting" className="p-2">
            <TargetingPanel
              domain={selectedDomain}
              liveCoupons={coupons.filter((c) => c._status === "LIVE")}
              enabled={activeTab === "targeting"}
            />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Mark as tested section */}
      <div className="border-top p-2">
        {lastTestedAt && (
          <div className="small text-muted mb-1">
            <CheckCircle size={12} className="text-success me-1" />
            Last tested {format(new Date(lastTestedAt), "yyyy-MM-dd HH:mm")}
            {testedByName && ` by ${testedByName}`}
          </div>
        )}
        <InputGroup size="sm">
          <Form.Control
            placeholder="Comment (required)..."
            value={testComment}
            onChange={(e) => setTestComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && testComment.trim()) {
                onMarkTested(testComment.trim());
                setTestComment("");
              }
            }}
          />
          <Button
            variant="success"
            disabled={!testComment.trim() || markingTested}
            onClick={() => {
              onMarkTested(testComment.trim());
              setTestComment("");
            }}
          >
            {markingTested ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <CheckCircle size={14} className="me-1" />
                Confirm Tested
              </>
            )}
          </Button>
        </InputGroup>
      </div>
    </>
  );
};

type DealWithStatus = IKindredDealSummary & { _status: EntityStatus };
type OfferWithStatus = IKindredOfferSummary & { _status: EntityStatus };

const DealsTable: React.FC<{
  deals: DealWithStatus[];
  onSelect: (id: string) => void;
  selectedId: string | null;
}> = ({ deals, onSelect, selectedId }) => {
  const sorted = useMemo(() => {
    return [...deals].sort(
      (a, b) => statusPriority[a._status] - statusPriority[b._status]
    );
  }, [deals]);

  if (sorted.length === 0)
    return <div className="text-muted small py-2">No deals found</div>;
  return (
    <Table hover size="sm" className="mb-0">
      <thead>
        <tr>
          <th>Name</th>
          <th>Brand</th>
          <th>Status</th>
          <th>Source</th>
          <th>Valid To</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((deal) => (
          <tr
            key={deal.id}
            style={{ cursor: "pointer" }}
            className={deal.id === selectedId ? "table-active" : ""}
            onClick={() => onSelect(deal.id)}
          >
            <td className="small text-truncate" style={{ maxWidth: 200 }}>
              {deal.name}
            </td>
            <td className="small">
              <Link
                to={`/kindred/brands/${deal.brandId}`}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
                className="text-decoration-none"
              >
                {deal.brandName}
              </Link>
            </td>
            <td>{entityStatusBadge(deal._status)}</td>
            <td className="small">{deal.source}</td>
            <td className="small">{formatDate(deal.endDate)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const CouponsTable: React.FC<{
  coupons: CouponWithStatus[];
  onSelect: (id: string) => void;
  selectedId: string | null;
  flaggedCodes: Set<string>;
}> = ({ coupons, onSelect, selectedId, flaggedCodes }) => {
  const sorted = useMemo(() => {
    return [...coupons].sort(
      (a, b) => statusPriority[a._status] - statusPriority[b._status]
    );
  }, [coupons]);

  if (sorted.length === 0)
    return <div className="text-muted small py-2">No coupons found</div>;
  return (
    <Table hover size="sm" className="mb-0">
      <thead>
        <tr>
          <th>Code</th>
          <th>Brand</th>
          <th>Summary</th>
          <th>Status</th>
          <th>Valid To</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((c) => {
          const needsReview =
            c._status === "LIVE" && flaggedCodes.has(c.code.toUpperCase());
          return (
            <tr
              key={c.id}
              style={{ cursor: "pointer" }}
              className={c.id === selectedId ? "table-active" : ""}
              onClick={() => onSelect(c.id)}
            >
              <td className="small fw-semibold">
                {needsReview && (
                  <span title="Failed in recent agentic test — review recommended">
                    <AlertCircle size={14} className="text-warning me-1" />
                  </span>
                )}
                {c.code}
              </td>
              <td className="small">
                <Link
                  to={`/kindred/brands/${c.brandId}`}
                  target="_blank"
                  onClick={(e) => e.stopPropagation()}
                  className="text-decoration-none"
                >
                  {c.brandName}
                </Link>
              </td>
              <td className="small text-truncate" style={{ maxWidth: 200 }}>
                {c.summary}
              </td>
              <td>{entityStatusBadge(c._status)}</td>
              <td className="small">{formatDate(c.validTo)}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

const OffersTable: React.FC<{
  offers: OfferWithStatus[];
  onSelect: (id: string) => void;
  selectedId: string | null;
}> = ({ offers, onSelect, selectedId }) => {
  const sorted = useMemo(() => {
    return [...offers].sort(
      (a, b) => statusPriority[a._status] - statusPriority[b._status]
    );
  }, [offers]);

  if (sorted.length === 0)
    return <div className="text-muted small py-2">No offers found</div>;
  return (
    <Table hover size="sm" className="mb-0">
      <thead>
        <tr>
          <th>Summary</th>
          <th>Brand</th>
          <th>Status</th>
          <th>Source</th>
          <th>Valid To</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((o) => (
          <tr
            key={o.id}
            style={{ cursor: "pointer" }}
            className={o.id === selectedId ? "table-active" : ""}
            onClick={() => onSelect(o.id)}
          >
            <td className="small text-truncate" style={{ maxWidth: 200 }}>
              {o.summary}
            </td>
            <td className="small">
              <Link
                to={`/kindred/brands/${o.brandId}`}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
                className="text-decoration-none"
              >
                {o.brandName}
              </Link>
            </td>
            <td>{entityStatusBadge(o._status)}</td>
            <td className="small">{o.source}</td>
            <td className="small">{formatDate(o.validTo)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

// ── Agentic Testing Panel ────────────────────────────────

type DateRangeOption = "24h" | "3d" | "7d";
type ResultStatusFilter =
  | "all"
  | "systemError"
  | "confirmedFailure"
  | "applied";

const DATE_RANGE_OPTIONS: { value: DateRangeOption; label: string }[] = [
  { value: "24h", label: "Last 24 hours" },
  { value: "3d", label: "Last 3 days" },
  { value: "7d", label: "Last 7 days" },
];

const RESULT_STATUS_OPTIONS: { value: ResultStatusFilter; label: string }[] = [
  { value: "all", label: "All Results" },
  { value: "applied", label: "Applied" },
  { value: "confirmedFailure", label: "Confirmed Failure" },
  { value: "systemError", label: "System Error" },
];

const getDateRangeStart = (range: DateRangeOption): Date => {
  const now = new Date();
  switch (range) {
    case "24h":
      return subDays(now, 1);
    case "3d":
      return subDays(now, 3);
    case "7d":
      return subDays(now, 7);
  }
};

const AgenticTestingPanel: React.FC<{
  domain: string | null;
  countryCode: string | null;
  liveCoupons: CouponWithStatus[];
  onSelectCoupon: (id: string) => void;
  selectedCouponId: string | null;
}> = ({
  domain,
  countryCode,
  liveCoupons,
  onSelectCoupon,
  selectedCouponId,
}) => {
  const [dateRange, setDateRange] = useState<DateRangeOption>("3d");
  const [resultStatus, setResultStatus] = useState<ResultStatusFilter>("all");
  const [page, setPage] = useState(1);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [dateRange, resultStatus, domain, countryCode]);

  const startDate = useMemo(
    () => getDateRangeStart(dateRange).toISOString(),
    [dateRange]
  );

  const queryParams = useMemo(() => {
    if (!domain) return null;
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", "25");
    params.set("merchantUrl", domain);
    if (countryCode) params.set("countryCode", countryCode);
    params.set("startDate", startDate);
    if (resultStatus === "systemError") params.set("systemError", "true");
    if (resultStatus === "confirmedFailure")
      params.set("confirmedFailure", "true");
    if (resultStatus === "applied") params.set("applied", "true");
    return params.toString();
  }, [domain, countryCode, page, startDate, resultStatus]);

  const { data, loading } = useGet<ICouponResultsResponse>(
    queryParams ? `results/coupons?${queryParams}` : null,
    !!queryParams
  );

  if (!domain) {
    return (
      <div className="text-muted small py-2">
        Select a domain to view agentic test results
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex gap-2 mb-2">
        <Form.Select
          size="sm"
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value as DateRangeOption)}
          style={{ width: 160 }}
        >
          {DATE_RANGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          size="sm"
          value={resultStatus}
          onChange={(e) =>
            setResultStatus(e.target.value as ResultStatusFilter)
          }
          style={{ width: 180 }}
        >
          {RESULT_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Form.Select>
      </div>

      {loading ? (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" />
        </div>
      ) : !data || data.results.length === 0 ? (
        <div className="text-muted small py-2">
          No agentic test results found
        </div>
      ) : (
        <>
          <div className="small text-muted mb-1">
            {data.pagination.total} result
            {data.pagination.total !== 1 ? "s" : ""} found
          </div>
          <Table hover size="sm" className="mb-2">
            <thead>
              <tr>
                <th>Code</th>
                <th>Status</th>
                <th>Savings</th>
                <th>Merchant</th>
                <th>Tested</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((r) => (
                <tr key={r._id}>
                  <td className="small fw-semibold">{r.code}</td>
                  <td>
                    <CouponResultStatusBadge result={r} />
                  </td>
                  <td className="small">
                    {r.savings > 0 ? (
                      <span className="text-success">
                        {r.testResult?.currency || ""} {r.savings.toFixed(2)}
                        {r.percentageSavings != null && (
                          <> ({r.percentageSavings.toFixed(1)}%)</>
                        )}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="small text-truncate" style={{ maxWidth: 120 }}>
                    {r.merchant?.name || "—"}
                  </td>
                  <td className="small">
                    {r.testResult?.startedAt
                      ? format(new Date(r.testResult.startedAt), "MMM d, HH:mm")
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {data.pagination.totalPages > 1 && (
            <div className="d-flex justify-content-center gap-2 mb-2">
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Prev
              </Button>
              <span className="small align-self-center">
                {page} / {data.pagination.totalPages}
              </span>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={page >= data.pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Live coupons for this domain */}
      {liveCoupons.length > 0 && (
        <div className="mt-3">
          <h6 className="small fw-semibold border-top pt-2">
            Live Coupons ({liveCoupons.length})
          </h6>
          <Table hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Code</th>
                <th>Brand</th>
                <th>Summary</th>
                <th>Valid To</th>
              </tr>
            </thead>
            <tbody>
              {liveCoupons.map((c) => (
                <tr
                  key={c.id}
                  style={{ cursor: "pointer" }}
                  className={c.id === selectedCouponId ? "table-active" : ""}
                  onClick={() => onSelectCoupon(c.id)}
                >
                  <td className="small fw-semibold">{c.code}</td>
                  <td className="small">{c.brandName}</td>
                  <td className="small text-truncate" style={{ maxWidth: 200 }}>
                    {c.summary}
                  </td>
                  <td className="small">{formatDate(c.validTo)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

const CouponResultStatusBadge: React.FC<{
  result: ICouponResultWithMerchant;
}> = ({ result }) => {
  if (result.systemError) {
    return (
      <div>
        <Badge bg="danger">
          <AlertOctagon size={10} className="me-1" />
          System Error
        </Badge>
        {result.errorMessage && (
          <div className="text-danger small mt-1" style={{ fontSize: 11 }}>
            {result.errorMessage}
          </div>
        )}
      </div>
    );
  }
  if (result.confirmedFailure) {
    return (
      <div>
        <Badge bg="warning" text="dark">
          <AlertTriangle size={10} className="me-1" />
          Failed
        </Badge>
        {result.errorMessage && (
          <div className="text-muted small mt-1" style={{ fontSize: 11 }}>
            {result.errorMessage}
          </div>
        )}
      </div>
    );
  }
  if (result.applied) {
    return (
      <Badge bg="success">
        <CheckCircle size={10} className="me-1" />
        Applied
      </Badge>
    );
  }
  if (result.notApplicableToBasket) {
    return <Badge bg="secondary">N/A to Basket</Badge>;
  }
  if (result.errorMessage) {
    return (
      <div>
        <Badge bg="light" text="dark">
          Not Applied
        </Badge>
        <div className="text-muted small mt-1" style={{ fontSize: 11 }}>
          {result.errorMessage}
        </div>
      </div>
    );
  }
  return (
    <Badge bg="light" text="dark">
      Not Applied
    </Badge>
  );
};
